/** A2A → Delegations: every grant chain across topics, with the kill switch. */
import React, { useCallback, useEffect, useState } from 'react';
import { Icon, Modal } from '../../components/ui';
import GrantTree from '../../components/a2a/GrantTree';
import ActorSwitch from '../../components/a2a/ActorSwitch';
import { a2a, Grant } from '../../lib/a2aApi';
import { usePermission } from '../../hooks/usePermission';
import '../../components/a2a/a2a.css';
import { Trans, useTranslation } from 'react-i18next';

const A2ADelegations: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { t: tr } = useTranslation();
  const canWrite = usePermission('update:policy');
  const [grants, setGrants] = useState<Grant[]>([]); const [showDead, setShowDead] = useState(true);
  const [revoking, setRevoking] = useState<{ g: Grant; count: number; downstream: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null); const [note, setNote] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => { try { setGrants((await a2a.grants(apiUrl)).grants); setError(null); } catch (e: any) { setError(e.message); } }, [apiUrl]);
  useEffect(() => { void load(); const t = setInterval(() => { setNow(Date.now()); void load(); }, 4000); return () => clearInterval(t); }, [load]);

  const visible = showDead ? grants : grants.filter((g) => g.status === 'active');
  const roots = new Set(visible.map((g) => g.root_grant_id));
  const confirm = async () => {
    if (!revoking) return;
    try { const r = await a2a.revoke(apiUrl, revoking.g.grant_id, 'kill switch'); setNote(`${r.revoked} grants revoked (feed epoch ${r.epoch}).`); setRevoking(null); await load(); } catch (e: any) { setError(e.message); }
  };

  return (
    <div className="page-content">
      <div className="page-title"><div><h1>{tr('pages.a2a.a2ADelegations.delegations')}</h1><p className="subtitle">{tr('pages.a2a.a2ADelegations.everyChainOfAuthorityAHumans')}</p></div>
        <div className="a2a-row"><ActorSwitch apiUrl={apiUrl} onChange={load} /><label className="a2a-muted"><input type="checkbox" checked={showDead} onChange={(e) => setShowDead(e.target.checked)} /> {tr('pages.a2a.a2ADelegations.showRevokedExpired')}</label></div></div>
      {error && <div className="notice error"><Icon name="warning" /> {error}</div>}
      {note && <div className="notice success"><Icon name="checkCircle" /> {note}</div>}
      {roots.size === 0 && <div className="ent-empty">{tr('pages.a2a.a2ADelegations.noDelegationChainsYetRunThe')}</div>}
      {Array.from(roots).map((r) => {
        const g = visible.filter((x) => x.root_grant_id === r);
        return <div className="ent-card" key={r} style={{ marginBottom: 14 }}><div className="ent-card-head"><Icon name="key" /> {tr('pages.a2a.a2ADelegations.chainFor')} <strong style={{ marginLeft: 4 }}>{g[0].principal_user_id}</strong><span className="a2a-muted" style={{ marginLeft: 8 }}>{g[0].topic_id}</span></div>
          <div className="ent-card-body"><GrantTree grants={g} canWrite={canWrite} now={now} onRevoke={async (x) => { const p = await a2a.revokePreview(apiUrl, x.grant_id); setRevoking({ g: x, count: p.active, downstream: p.downstream.map((d) => d.audience) }); }} /></div></div>;
      })}
      {revoking && <Modal title={tr('pages.a2a.a2ADelegations.revokeDelegationSubtree')} icon="deny" onClose={() => setRevoking(null)}
        footer={<div className="caw-foot"><button className="ent-btn" onClick={() => setRevoking(null)}>{tr('pages.a2a.a2ADelegations.cancel')}</button><button className="ent-btn primary" onClick={confirm}>{tr('pages.a2a.a2ADelegations.revokeGrants', { count: revoking.count })}</button></div>}>
        <p>
          <Trans
            i18nKey="pages.a2a.a2ADelegations.grantsInvalidated"
            count={revoking.count}
            values={{ count: revoking.count, audience: revoking.g.audience_agent_id, downstream: revoking.downstream.length ? tr('pages.a2a.a2ADelegations.andDownstream', { names: revoking.downstream.join(', ') }) : '' }}
            components={{ b: <strong /> }}
          />
        </p>
      </Modal>}
    </div>
  );
};
export default A2ADelegations;
